import React, { useState, useCallback } from 'react';
import {
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  Box,
  Icon,
  useColorModeValue,
  Badge,
  SimpleGrid,
  Button,
  Alert,
  AlertIcon,
  Progress,
  Divider,
  IconButton,
  useToast,
  Input,
  Flex,
} from '@chakra-ui/react';
import { 
  FiUpload, 
  FiFile, 
  FiFileText, 
  FiShield, 
  FiAward, 
  FiX,
  FiEye,
  FiCheckCircle,
  FiAlertCircle
} from 'react-icons/fi';
import { RegistrationData } from '../index';

interface DocumentUploadProps {
  data: RegistrationData;
  updateData: (data: RegistrationData) => void;
  onNext: () => void;
  onPrevious: () => void;
}

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  category: string;
  uploadDate: Date;
  file: File;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  data,
  updateData,
  onNext,
  onPrevious,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  
  const toast = useToast();
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const dropzoneBg = useColorModeValue('gray.50', 'gray.700');
  const dragActiveBg = useColorModeValue('blue.50', 'blue.900');
  const uploadProgressBg = useColorModeValue('blue.50', 'blue.900');
  const summarySuccessBg = useColorModeValue('green.50', 'green.900');

  const documentCategories = [
    {
      id: 'business_license',
      title: 'Business License',
      icon: FiShield,
      color: 'blue',
      description: 'Current business license and permits',
      examples: 'Business license, operating permits, trade licenses',
      required: false,
      accepts: '.pdf,.jpg,.jpeg,.png'
    },
    {
      id: 'insurance',
      title: 'Insurance Certificates',
      icon: FiShield,
      color: 'green',
      description: 'Liability and workers compensation insurance',
      examples: 'General liability, professional liability, workers comp',
      required: false,
      accepts: '.pdf,.jpg,.jpeg,.png'
    },
    {
      id: 'certifications',
      title: 'Quality Certifications',
      icon: FiAward,
      color: 'purple',
      description: 'Industry quality and safety certifications',
      examples: 'ISO 9001, GREENGUARD, BIFMA, safety certifications',
      required: false,
      accepts: '.pdf,.jpg,.jpeg,.png'
    },
    {
      id: 'financial_documents',
      title: 'Financial Documents',
      icon: FiFileText,
      color: 'orange',
      description: 'Financial statements and credit references',
      examples: 'Bank statements, financial reports, credit references',
      required: false,
      accepts: '.pdf,.jpg,.jpeg,.png'
    },
    {
      id: 'diversity_certs',
      title: 'Diversity Certifications',
      icon: FiAward,
      color: 'pink',
      description: 'Minority and diversity business certifications',
      examples: 'MBE, WBE, DBE, VOSB, SDB certifications',
      required: false,
      accepts: '.pdf,.jpg,.jpeg,.png'
    },
    {
      id: 'other',
      title: 'Additional Documents',
      icon: FiFile,
      color: 'gray',
      description: 'Any other supporting business documents',
      examples: 'Contracts, references, additional certifications',
      required: false,
      accepts: '.pdf,.jpg,.jpeg,.png,.doc,.docx'
    }
  ];

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = (e: React.DragEvent, category?: string) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files, category || 'other');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, category: string) => {
    const files = Array.from(e.target.files || []);
    handleFileUpload(files, category);
  };

  const handleFileUpload = async (files: File[], category: string) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Invalid File Type',
          description: `${file.name} is not a supported file type.`,
          status: 'error',
          duration: 3000,
        });
        continue;
      }

      if (file.size > maxSize) {
        toast({
          title: 'File Too Large',
          description: `${file.name} exceeds the 10MB size limit.`,
          status: 'error',
          duration: 3000,
        });
        continue;
      }

      const fileId = `${Date.now()}-${Math.random()}`;
      
      // Simulate upload progress
      setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));
      
      // Simulate upload with progress
      for (let progress = 0; progress <= 100; progress += 10) {
        await new Promise(resolve => setTimeout(resolve, 50));
        setUploadProgress(prev => ({ ...prev, [fileId]: progress }));
      }

      const newFile: UploadedFile = {
        id: fileId,
        name: file.name,
        size: file.size,
        type: file.type,
        category,
        uploadDate: new Date(),
        file
      };

      setUploadedFiles(prev => [...prev, newFile]);
      setUploadProgress(prev => {
        const updated = { ...prev };
        delete updated[fileId];
        return updated;
      });

      toast({
        title: 'Upload Complete',
        description: `${file.name} uploaded successfully.`,
        status: 'success',
        duration: 2000,
      });
    }
  };

  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
    toast({
      title: 'File Removed',
      description: 'Document has been removed from your uploads.',
      status: 'info',
      duration: 2000,
    });
  };

  const getFilesByCategory = (categoryId: string) => {
    return uploadedFiles.filter(file => file.category === categoryId);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const hasAnyDocuments = uploadedFiles.length > 0;

  return (
    <VStack spacing={8} align="stretch">
      {/* Document Categories */}
      <Box>
        <VStack spacing={6} align="stretch">
          <HStack spacing={3}>
            <Icon as={FiFileText} color="blue.500" boxSize={5} />
            <Text fontSize="lg" fontWeight="semibold">
              Document Categories
            </Text>
            <Badge colorScheme="blue" variant="outline" ml="auto">
              Optional
            </Badge>
          </HStack>

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
            {documentCategories.map((category) => {
              const categoryFiles = getFilesByCategory(category.id);
              const hasFiles = categoryFiles.length > 0;
              
              return (
                <Card 
                  key={category.id} 
                  borderWidth={2}
                  borderColor={hasFiles ? 'green.200' : 'red.200'}
                  bg={hasFiles ? 'green.50' : 'red.50'}
                >
                  <CardBody p={6}>
                    <VStack spacing={4} align="stretch">
                      <HStack spacing={3}>
                        <Icon as={category.icon} color={`${category.color}.500`} boxSize={5} />
                        <VStack align="start" spacing={1} flex={1}>
                          <HStack>
                            <Text fontWeight="semibold">
                              {category.title}
                            </Text>
                            {hasFiles && <Icon as={FiCheckCircle} color="green.500" />}
                          </HStack>
                          <Text fontSize="sm" color="gray.600">
                            {category.description}
                          </Text>
                          <Text fontSize="xs" color="gray.500" fontStyle="italic">
                            Examples: {category.examples}
                          </Text>
                        </VStack>
                      </HStack>

                      {/* Upload Button */}
                      <Box>
                        <Input
                          type="file"
                          multiple
                          accept={category.accepts}
                          onChange={(e) => handleFileSelect(e, category.id)}
                          style={{ display: 'none' }}
                          id={`upload-${category.id}`}
                        />
                        <Button
                          as="label"
                          htmlFor={`upload-${category.id}`}
                          size="lg"
                          leftIcon={<Icon as={FiUpload} />}
                          colorScheme={category.color}
                          variant={hasFiles ? 'outline' : 'solid'}
                          w="full"
                          cursor="pointer"
                        >
                          {hasFiles ? 'Add More Files' : 'Upload Documents'}
                        </Button>
                      </Box>

                      {/* Uploaded Files */}
                      {hasFiles && (
                        <VStack spacing={3} align="stretch">
                          <Divider />
                          <Text fontSize="sm" fontWeight="medium" color="green.700">
                            Uploaded Files ({categoryFiles.length})
                          </Text>
                          {categoryFiles.map((file) => (
                            <HStack key={file.id} spacing={3} p={3} bg="white" borderRadius="md" borderWidth={1}>
                              <Icon as={FiFile} color="gray.500" />
                              <VStack align="start" spacing={1} flex={1}>
                                <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                                  {file.name}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  {formatFileSize(file.size)}
                                </Text>
                              </VStack>
                              <HStack spacing={1}>
                                <IconButton
                                  size="sm"
                                  aria-label="View file"
                                  icon={<FiEye />}
                                  variant="ghost"
                                  onClick={() => {
                                    toast({
                                      title: 'Preview',
                                      description: 'File preview functionality would open here',
                                      status: 'info',
                                      duration: 2000,
                                    });
                                  }}
                                />
                                <IconButton
                                  size="sm"
                                  aria-label="Remove file"
                                  icon={<FiX />}
                                  variant="ghost"
                                  colorScheme="red"
                                  onClick={() => removeFile(file.id)}
                                />
                              </HStack>
                            </HStack>
                          ))}
                        </VStack>
                      )}
                    </VStack>
                  </CardBody>
                </Card>
              );
            })}
          </SimpleGrid>
        </VStack>
      </Box>

      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <Card bg={uploadProgressBg} borderColor="blue.200">
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack spacing={3}>
                <Icon as={FiUpload} color="blue.500" />
                <Text fontWeight="semibold" color="blue.700">
                  Uploading Files...
                </Text>
              </HStack>
              {Object.entries(uploadProgress).map(([fileId, progress]) => (
                <Box key={fileId}>
                  <HStack justify="space-between" mb={1}>
                    <Text fontSize="sm">Processing...</Text>
                    <Text fontSize="sm" fontWeight="medium">{Math.round(progress)}%</Text>
                  </HStack>
                  <Progress value={progress} size="md" colorScheme="blue" borderRadius="full" />
                </Box>
              ))}
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Summary */}
      {uploadedFiles.length > 0 && (
        <Card bg={summarySuccessBg} borderColor="green.200">
          <CardBody>
            <HStack spacing={3}>
              <Icon as={FiCheckCircle} color="green.500" />
              <VStack align="start" spacing={1}>
                <Text fontWeight="semibold" color="green.700">
                  {uploadedFiles.length} document{uploadedFiles.length > 1 ? 's' : ''} uploaded successfully
                </Text>
                <Text fontSize="sm" color="green.600">
                  Documents uploaded successfully. You can continue to the next step.
                </Text>
              </VStack>
              <Badge colorScheme="green" ml="auto">
                Ready to continue
              </Badge>
            </HStack>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
};

export default DocumentUpload; 